import { useCallback, useEffect, useState } from 'react';

import type { PendingRegistrationRow } from '../../features/committee/application/ports/progressPort';
import type { EmployeeCandidate } from '../../features/committee/application/ports/registrationReviewPort';
import { RegistrationReviewPage } from '../../pages/RegistrationReviewPage';
import { formatDateAr } from '../../shared/format/dateFormat';

import { useServices } from '../providers/servicesContext';

/**
 * Connects the review queue to its ports.
 *
 * The queue is re-read after every decision rather than edited in place: the
 * server decides, and a row removed locally because the call returned would
 * claim an outcome this screen did not witness.
 */
export function RegistrationReviewScreen({
  branchNames,
}: {
  readonly branchNames: Readonly<Record<string, string>>;
}) {
  const { progress, registrationReview } = useServices();

  const [pending, setPending] = useState<readonly PendingRegistrationRow[]>([]);
  const [busyUid, setBusyUid] = useState<string | null>(null);
  const [errorAr, setErrorAr] = useState<string | null>(null);
  const [noticeAr, setNoticeAr] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    progress
      .listPendingRegistrations()
      .then((rows) => {
        if (!cancelled) setPending(rows);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setErrorAr(error instanceof Error ? error.message : 'تعذّر تحميل الطلبات.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [progress, reloadToken]);

  const lookUp = useCallback(
    async (employeeNumber: string): Promise<EmployeeCandidate | null> => {
      setErrorAr(null);
      try {
        return await registrationReview.findByEmployeeNumber(employeeNumber);
      } catch (error) {
        setErrorAr(error instanceof Error ? error.message : 'تعذّر البحث.');
        return null;
      }
    },
    [registrationReview],
  );

  const decide = useCallback(
    (applicantUid: string, run: () => Promise<void>, doneAr: string) => {
      setBusyUid(applicantUid);
      setErrorAr(null);
      setNoticeAr(null);

      void run()
        .then(() => {
          setNoticeAr(doneAr);
          setReloadToken((token) => token + 1);
        })
        .catch((error: unknown) => {
          setErrorAr(error instanceof Error ? error.message : 'تعذّر إتمام العملية.');
        })
        .finally(() => {
          setBusyUid(null);
        });
    },
    [],
  );

  return (
    <RegistrationReviewPage
      pending={pending.map((row) => ({
        authUid: row.authUid,
        fullNameAr: row.fullNameAr,
        email: row.email,
        phone: row.phone,
        branchNameAr: branchNames[row.branchId] ?? row.branchId,
        reason: row.reason,
        submittedAtAr: formatDateAr(row.submittedAt),
      }))}
      busyUid={busyUid}
      errorAr={errorAr}
      noticeAr={noticeAr}
      onLookUp={lookUp}
      onApprove={(applicantUid, employeeId) => {
        decide(
          applicantUid,
          () => registrationReview.approve(applicantUid, employeeId),
          'اعتُمد الطلب ورُبط الحساب بسجله.',
        );
      }}
      onReject={(applicantUid, reasonAr) => {
        decide(
          applicantUid,
          () => registrationReview.reject(applicantUid, reasonAr),
          'رُفض الطلب وأُبلغ صاحبه بالسبب.',
        );
      }}
    />
  );
}
